import {
  createRouter,
  createWebHashHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw
} from 'vue-router';

import {
  hasAssetsPreloaded,
  preloadCriticalAssets
} from '@/composables/common/useAssetPreload';
import { useAuthStore } from '@/stores/auth';
import { useUtilsStore } from '@/stores/utils';
import { AUTHORITY_TYPE } from '@/utils/constants';
import mixpanel from 'mixpanel-browser';
// import { storeToRefs } from 'pinia';

const NAVIGATION_SESSION_KEY = 'eve_pro_vueRouterNavigated';

const routes: RouteRecordRaw[] = [
  // ログイン前のユーザーはこちらのルートにリダイレクトされます
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: {
      jpName: 'ログイン',
      enterClass: 'animate__animated animate__fadeInLeft'
    }
  },
  {
    path: '/matchlist',
    name: 'matchlist',
    component: () => import('@/views/MatchListPage.vue'),
    meta: {
      jpName: '試合一覧'
    }
  },
  {
    path: '/matchprogress/:id',
    name: 'matchprogress',
    component: () => import('@/views/game/MatchProgressPage.vue'),
    meta: {
      jpName: '試合詳細'
    }
  },
  {
    path: '/matchdetail',
    name: 'matchdetail',
    component: () => import('@/views/game/MatchDetailPage.vue'),
    meta: {
      jpName: '試合情報 イニング詳細'
    }
  },
  {
    path: '/videolist',
    name: 'videolist',
    component: () => import('@/views/video/VideoListPage.vue'),
    meta: {
      jpName: '映像一覧 ',
      category: 'video'
    }
  },
  {
    path: '/videolist/:id/videolabel',
    name: 'videolabel',
    component: () => import('@/views/video/VideoLabelPage.vue'),
    meta: {
      jpName: '映像 ラベル編集・追加BASSデータ読み込み ',
      preventDirectAccess: true,
      category: 'video'
    }
  },
  {
    path: '/videolist/:id/videoedit',
    name: 'videoedit',
    component: () => import('@/views/video/VideoEditPage.vue'),
    meta: {
      jpName: '映像  設定変更',
      preventDirectAccess: true,
      category: 'video'
    }
  },
  {
    path: '/videoregist',
    name: 'videoregist',
    component: () => import('@/views/video/VideoRegistPage.vue'),
    meta: {
      jpName: '映像 新規映像追加',
      preventDirectAccess: true,
      category: 'video'
    }
  },
  {
    path: '/comparison',
    name: 'comparison',
    component: () => import('@/views/comparison/ComparisonPage.vue'),
    meta: {
      jpName: '動画比較'
    }
  },
  {
    path: '/comparison/select/:side',
    name: 'selectComparisonVideo',
    component: () => import('@/views/comparison/SelectComparisonVideoPage.vue'),
    meta: {
      jpName: '動画比較 比較映像選択',
      preventDirectAccess: true
    }
  },
  {
    path: '/documentlist/:dir_id?',
    name: 'documentlist',
    component: () => import('@/views/document/DocumentListPage.vue'),
    meta: {
      jpName: 'ドキュメント一覧 '
    }
  },
  {
    path: '/singleplayvideo',
    name: 'singleplayvideo',
    component: () => import('@/views/singlelPlayVideo/SinglePlayVideoPage.vue'),
    meta: {
      jpName: '一球映像'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundPage.vue')
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

router.beforeEach(async (to, from, next) => {
  const hasNavigated = sessionStorage.getItem(NAVIGATION_SESSION_KEY);
  const authStore = useAuthStore();

  if (to.meta.preventDirectAccess && !hasNavigated) {
    if (!from.name) {
      switch (to.meta.category) {
        case 'video':
          next('/videolist');
          break;
        default:
          next('/matchlist');
      }
    }
  }

  // ログインページはセッションチェックをスキップ
  if (to.path === '/login') {
    const authStore = useAuthStore();
    if (authStore.isLoggedIn && authStore.user?.id) {
      next('/matchlist');
    } else {
      // 未ログインの場合は、そのままログインページを表示
      next();
    }
  }

  // セッション情報を取得（トークンがある場合は認証情報を復元）
  try {
    await authStore.session();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // セッションエラーの場合はログイン画面へ
    console.error(error.message);
    if (to.path !== '/login') {
      return next('/login');
    }
    return next();
  }

  const isLoggedIn = authStore.isLoggedIn;

  // 未ログインの場合はログインページへ
  if (!isLoggedIn) {
    return next('/login');
  }

  // 権限がいるページへのURL直叩き対策
  if (['videoregist', 'videolabel'].includes(to.path.split('/').pop() || '')) {
    if (authStore.userAuthority !== AUTHORITY_TYPE.admin.value) {
      return next('/videolist');
    }
  }

  // 初回ログイン時：アセットをプリロードするまで TheLoading を表示
  if (to.path !== '/login' && !hasAssetsPreloaded()) {
    const utilsStore = useUtilsStore();
    utilsStore.incrementLoading();
    try {
      await preloadCriticalAssets();
    } finally {
      utilsStore.decrementLoading();
    }
  }

  return next();
});

router.afterEach((to: RouteLocationNormalized) => {
  // サイト内で一度でもナビゲーションが発生したらフラグを立てる
  // これにより、次回以降のリロードは許可される
  if (to.name) {
    // 正常なルートへの遷移後
    sessionStorage.setItem(NAVIGATION_SESSION_KEY, 'true');
    try {
      mixpanel?.track('Navigation', { route: to.path });
       
    } catch (_) {}
  }
});

export default router;
